import { useCallback, useEffect, useMemo, useState } from "react";
import { apiUrl } from "utils/apiUrl";
import { clientSafeMessage } from "utils/clientSafeMessage";
import { resolveEffectiveClientId } from "utils/effectiveClientId";
import { accountMatchesSubscriptionPath, buildSlotsOwnedUrl } from "utils/subscriptionPath";

function mapSlaveRow(a) {
  return {
    id: a.id || `slot-${a.slot}`,
    slot: Number(a.slot),
    subscriptionPath: a.subscriptionPath || null,
    accountType: a.accountType || "MT5",
    accountNumber: a.accountNumber || "",
    server: a.server || "",
    copierDisplayName: a.copierDisplayName || "",
    tradeCopierAccountId: a.tradeCopierAccountId ?? null,
    linkedToCopier: a.linkedToCopier === true,
    brokerConnected: a.brokerConnected === true,
    balanceDisplay: a.balanceDisplay ?? null,
  };
}

/**
 * One Duplikium list call for the subscription hub (Path 1 + Path 2 summaries).
 */
export function useSubscriptionPathsSummary(session) {
  const effectiveClientId = resolveEffectiveClientId(session);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [paidSlotsPath1, setPaidSlotsPath1] = useState(0);
  const [paidSlotsPath2, setPaidSlotsPath2] = useState(0);
  const [awaitingApprovalPath1, setAwaitingApprovalPath1] = useState(0);
  const [awaitingApprovalPath2, setAwaitingApprovalPath2] = useState(0);
  const [partnerStatus, setPartnerStatus] = useState("");

  const path1Accounts = useMemo(
    () => allRows.filter((r) => accountMatchesSubscriptionPath(r, "path1")),
    [allRows],
  );
  const path2Accounts = useMemo(
    () => allRows.filter((r) => accountMatchesSubscriptionPath(r, "path2")),
    [allRows],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(apiUrl("/api/client/copy-list-slaves"), {
        credentials: "include",
      });
      const ct = res.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        throw new Error(
          "Service temporarily unavailable. Please refresh the page in a moment.",
        );
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        if (res.status === 401 && effectiveClientId) {
          throw new Error(
            "Your session has expired. Click Log Out, then sign in again.",
          );
        }
        throw new Error(
          clientSafeMessage(
            data.message,
            "Could not load your trading accounts.",
          ),
        );
      }
      const rows = Array.isArray(data.accounts) ? data.accounts : [];
      setAllRows(rows.map(mapSlaveRow));
    } catch (e) {
      setError(
        clientSafeMessage(e?.message, "Could not load your trading accounts."),
      );
    } finally {
      setLoading(false);
    }
  }, [effectiveClientId]);

  useEffect(() => {
    void refresh();
  }, [refresh, effectiveClientId]);

  useEffect(() => {
    if (!effectiveClientId) return undefined;
    const id = window.setInterval(() => void refresh(), 90000);
    return () => window.clearInterval(id);
  }, [effectiveClientId, refresh]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [r1, r2, partnerRes] = await Promise.all([
          fetch(apiUrl(buildSlotsOwnedUrl("path1")), { credentials: "include" }),
          fetch(apiUrl(buildSlotsOwnedUrl("path2")), { credentials: "include" }),
          fetch(apiUrl("/api/client/partner-broker-status"), {
            credentials: "include",
          }),
        ]);
        if (!cancelled && r1.ok) {
          const d1 = await r1.json().catch(() => ({}));
          const n1 = Number(d1?.paidSlotsCount);
          if (Number.isFinite(n1) && n1 >= 0) setPaidSlotsPath1(n1);
          const a1 = Number(d1?.awaitingApprovalCount);
          if (Number.isFinite(a1) && a1 >= 0) setAwaitingApprovalPath1(a1);
        }
        if (!cancelled && r2.ok) {
          const d2 = await r2.json().catch(() => ({}));
          const n2 = Number(d2?.paidSlotsCount);
          if (Number.isFinite(n2) && n2 >= 0) setPaidSlotsPath2(n2);
          const a2 = Number(d2?.awaitingApprovalCount);
          if (Number.isFinite(a2) && a2 >= 0) setAwaitingApprovalPath2(a2);
        }
        if (!cancelled && partnerRes.ok) {
          const pd = await partnerRes.json().catch(() => ({}));
          if (pd?.ok && pd.latest?.status) {
            setPartnerStatus(String(pd.latest.status).toLowerCase());
          }
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveClientId]);

  return {
    path1Accounts,
    path2Accounts,
    loading,
    error,
    refresh,
    paidSlotsPath1,
    paidSlotsPath2,
    awaitingApprovalPath1,
    awaitingApprovalPath2,
    partnerStatus,
    effectiveClientId,
  };
}
